if (!customElements.get('collection-carousel')) {
  class CollectionCarousel extends HTMLElement {
    constructor() {
      super();
      this.sliderInstance = null;
      this.resizeTimer = null;
      this.onResize = this.onResize.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.selectors = {
        slider: '.collection-carousel__slider',
        items: '.collection-carousel__items',
        item: '.collection-carousel__item',
        prev: '.collection-carousel__control--prev',
        next: '.collection-carousel__control--next',
        pagination: '.collection-carousel__pagination',
        controls: '.collection-carousel__controls',
      };
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.sliderEl = this.querySelector(this.selectors.slider);
      this.itemsWrapper = this.querySelector(this.selectors.items);
      this.prevButton = this.querySelector(this.selectors.prev);
      this.nextButton = this.querySelector(this.selectors.next);
      this.pagination = this.querySelector(this.selectors.pagination);
      this.controls = this.querySelector(this.selectors.controls);

      if (typeof window.FoxTheme === 'undefined' || !FoxTheme.Swiper) {
        console.warn('FoxTheme.Swiper is required for collection-carousel section');
        this.hideControls();
        return;
      }

      this.columns = {
        mobile: Number(this.dataset.mobileColumns) || 1,
        tablet: Number(this.dataset.tabletColumns) || Number(this.dataset.mobileColumns) || 2,
        desktop: Number(this.dataset.desktopColumns) || Number(this.dataset.tabletColumns) || 3,
      };
      this.space = {
        base: Number(this.dataset.gap) || 16,
        desktop: Number(this.dataset.gapDesktop) || Number(this.dataset.gap) || 16,
      };

      this.enablePagination = this.dataset.enablePagination === 'true';

      this.init();

      window.addEventListener('resize', this.onResize);

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      this.destroySlider();
    }

    init() {
      const slides = Array.from(this.querySelectorAll(this.selectors.item));
      this.slideCount = slides.length;

      if (!this.sliderEl || !this.itemsWrapper || this.slideCount === 0) {
        this.destroySlider();
        return;
      }

      const shouldEnableSlider = this.slideCount > this.columns.desktop;

      if (shouldEnableSlider) {
        if (!this.sliderInstance) {
          this.setupSlider(slides);
        } else {
          this.sliderInstance.slider.update();
        }
      } else {
        this.destroySlider();
      }

      this.toggleSliderState(shouldEnableSlider && Boolean(this.sliderInstance));
      if (shouldEnableSlider && this.sliderInstance) {
        this.toggleControls();
      } else {
        this.hideControls();
      }
    }

    setupSlider(slides) {
      this.sliderEl.classList.add('swiper');
      this.itemsWrapper.classList.add('swiper-wrapper');
      slides.forEach((slide) => slide.classList.add('swiper-slide'));

      const sliderOptions = {
        slidesPerView: 2,
        spaceBetween: this.space.base,
        threshold: 2,
        watchOverflow: true,
        navigation: {
          nextEl: this.nextButton,
          prevEl: this.prevButton,
        },
        breakpoints: {
          768: {
            slidesPerView: this.columns.tablet,
            spaceBetween: this.space.base,
          },
          1024: {
            slidesPerView: this.columns.desktop,
            spaceBetween: this.space.desktop,
          },
        },
      };

      if (this.enablePagination && this.pagination) {
        sliderOptions.pagination = {
          el: this.pagination,
          clickable: true,
        };
      }

      this.sliderInstance = new window.FoxTheme.Carousel(this.sliderEl, sliderOptions);
      this.sliderInstance.init();

      const focusable = FoxTheme?.a11y?.getFocusableElements
        ? FoxTheme.a11y.getFocusableElements(this)
        : [];
      focusable.forEach((element) => {
        if (element.classList.contains('swiper-button')) return;
        if (element.dataset.carouselFocusBound === 'true') return;
        element.dataset.carouselFocusBound = 'true';
        element.addEventListener('focusin', () => {
          const slide = element.closest('.swiper-slide');
          if (!slide || !this.sliderInstance?.slider) return;
          const index = this.sliderInstance.slider.slides.indexOf(slide);
          if (index >= 0) {
            this.sliderInstance.slider.slideTo(index);
          }
        });
      });
    }

    destroySlider() {
      this.toggleSliderState(false);

      if (!this.sliderInstance) return;

      this.sliderInstance.slider.destroy(true, true);
      this.sliderInstance = null;

      this.sliderEl?.classList.remove('swiper');
      this.itemsWrapper?.classList.remove('swiper-wrapper');
      this.querySelectorAll(this.selectors.item).forEach((slide) => slide.classList.remove('swiper-slide'));
    }

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.init();
      }, 150);
    }

    toggleSliderState(enable) {
      if (enable) {
        this.classList.add('is-slider');
        this.itemsWrapper?.classList.add('is-slider');
      } else {
        this.classList.remove('is-slider');
        this.itemsWrapper?.classList.remove('is-slider');
      }

      if (!enable) {
        this.hideControls();
      }
    }

    toggleControls() {
      if (!this.sliderInstance) {
        this.hideControls();
        return;
      }

      const { slider } = this.sliderInstance;
      if (!slider) return;

      const base = slider.params.slidesPerView;
      const perView = base === 'auto' ? slider.slidesPerViewDynamic('current', true) : base;
      const isLocked = slider.isLocked || slider.slides.length <= perView;
      this.setControlsVisibility(!isLocked);
    }

    hideControls() {
      this.setControlsVisibility(false);
    }

    setControlsVisibility(visible) {
      if (this.nextButton) {
        this.nextButton.classList.toggle('is-hidden', !visible);
      }
      if (this.prevButton) {
        this.prevButton.classList.toggle('is-hidden', !visible);
      }
      if (this.pagination) {
        this.pagination.classList.toggle('is-hidden', !visible);
      }
      if (this.controls) {
        this.controls.classList.toggle('is-hidden', !visible);
      }
    }

    onBlockSelect(event) {
      if (event.detail.sectionId !== this.sectionId || !this.sliderInstance?.slider) return;
      const target = event.target;
      const blockElement = target?.closest(this.selectors.item) || document.getElementById(event.detail.blockId);
      if (!blockElement) return;
      const datasetIndex = Number(blockElement.dataset.index);
      if (!Number.isNaN(datasetIndex)) {
        this.sliderInstance.slider.slideTo(datasetIndex);
        return;
      }
      const slide = blockElement.closest(this.selectors.item);
      if (!slide) return;
      const slideIndex = this.sliderInstance.slider.slides.indexOf(slide);
      if (slideIndex >= 0) {
        this.sliderInstance.slider.slideTo(slideIndex);
      }
    }
  }

  customElements.define('collection-carousel', CollectionCarousel);
}
